import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { differenceInDays, format } from "date-fns";
import {
  Calendar as CalendarIcon,
  SlidersHorizontal,
  MoreHorizontal,
  Eye,
  Trash,
  Send,
  FileText,
  Clock,
  AlertTriangle,
  Filter,
  X,
  Wallet,
  CircleX,
  CheckCircle,
  CircleAlert,
  Mails,
  Loader2,
  Info,
  TriangleAlert,
  Copy,
} from "lucide-react";
import { api } from "@/utils/axiosInterceptor";
import { useInvoices } from "@/hooks/useInvoices";
import { useQueryClient } from "@tanstack/react-query";
import { invoiceList } from "@/types/invoices";
import { invoiceTypes } from "@/constants/invoiceTypes";
import { getInvoiceStatusLabel } from "@/constants/invoiceStatusLabels";
import { useAuth } from "@/hooks/useAuth";
import { useEInvoices } from "@/hooks/useEInvoices";
import { navigate } from "wouter/use-browser-location";
import { invoiceDetailsApi } from "@/types/invoiceDetails";
import { useToast } from "@/hooks/use-toast";
import { DocumentType } from "@/types/form";
import LoadingIndicator from "./LoadingIndicator";
import IncompleteProfile from "./incomplete-profile";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Callout } from "@radix-ui/themes";
import { useBusinessProfile } from "@/hooks/useBusinessProfile";
import { AxiosError } from "axios";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";
import { toLhdnDate, toLhdnTime } from "@/utils/lhdnDate";
import { isWithinDayRange } from "@/utils/dateRange";
import { resolveUomCode } from "@/lib/uom";
// Helper function for class names concatenation
const cn = (...classes: (string | boolean)[]) =>
  classes.filter(Boolean).join(" ");

const InvoiceTable = () => {
  const { formatTotalAmount } = useDecimalPrecision();
  const { toast } = useToast();
  const [filter, setFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [typeFilter, setTypeFilter] = React.useState<string>("all");
  const [tagFilter, setTagFilter] = React.useState("all");
  const [emailStatusFilter, setEmailStatusFilter] = React.useState("all");
  // Issuance Date range (filters on invoice.invoiceDate)
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  // Invoice Date range (filters on invoice.docDate)
  const [docDateRange, setDocDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [selectedInvoices, setSelectedInvoices] = React.useState<
    { id: string; status: string | undefined | null }[]
  >([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [visibleColumns, setVisibleColumns] = React.useState({
    supplier: true,
    buyer: true,
    codeNumber: true,
    postReferenceNumber: true,
    docDate: true,
    invoiceDate: true,
    invoiceTime: true,
    amount: true,
    status: true,
    emailStatus: true,
    selfServiceTag: true,
    uuid: true,
    actions: true,
  });
  const columnLabels: Record<string, string> = {
    supplier: "Supplier",
    buyer: "Buyer",
    codeNumber: "Code Number",
    postReferenceNumber: "POS Reference Number",
    docDate: "Invoice Date",
    invoiceDate: "Issuance Date",
    invoiceTime: "Invoice Time",
    amount: "Amount",
    status: "Status",
    emailStatus: "Email Status",
    selfServiceTag: "Self-Service Tag",
    uuid: "UUID",
    actions: "Actions",
  };
  const [dropdownOpenVisibleColumn, setDropdownOpenVisibleColumn] =
    React.useState(false);
  const [openDropdownRowId, setOpenDropdownRowId] = React.useState<
    string | null
  >(null);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [isCloneInvoiceLoading, setIsCloneInvoiceLoading] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const queryClient = useQueryClient();

  // Reset pagination when filters change
  const resetPagination = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    resetPagination();
  }, [filter, statusFilter, typeFilter, dateRange, docDateRange]);

  const {
    invoices,
    deleteInvoiceAsync,
    cloneInvoice,
    isLoading: invoicesIsLoading,
  } = useInvoices();

  const { user, isLoading: userIsLoding } = useAuth();

  const {
    submitEInvoice,
    isSubmitting,
    insertTemporarySubmissionStatus,
    syncInvoiceStatus,
    acquireSubmissionLock,
    releaseSubmissionLock,
    markPendingCheck,
    processEInvoice,
  } = useEInvoices();

  const { expiredDate } = useBusinessProfile(user?.businessId);

  const itemsPerPage = 10;

  // Get unique statuses from data
  const statuses = [
    "all",
    "Draft",
    "Submitting",
    "Submitted",
    "Valid",
    "Invalid",
    "PendingCheck",
    "DuplicateConflict",
    "Consolidated",
    "Cancelled",
  ];

  if (invoicesIsLoading || userIsLoding) {
    return <LoadingIndicator />;
  }

  if (!user || user.addressId === null) {
    return <IncompleteProfile />;
  }

  // Enhanced filter logic
  const filteredData = invoices?.filter((invoice: invoiceList) => {
    const supplierName = invoice.supplierName ?? "Not Found";
    const buyerName = invoice.buyerName ?? "Not Found";

    const matchesSearch =
      supplierName.toLowerCase().includes(filter.toLowerCase()) ||
      buyerName.toLowerCase().includes(filter.toLowerCase()) ||
      invoice.codeNumber.toLowerCase().includes(filter.toLowerCase()) ||
      invoice.postReferenceNumber
        ?.toLowerCase()
        .includes(filter.toLocaleLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (invoice.status === statusFilter && statusFilter != "Invalid") ||
      // specially handle consolidated invalid invoice to show consolidated
      (invoice.status === statusFilter &&
        !invoice.isConsolidated &&
        statusFilter == "Invalid") ||
      (invoice.isConsolidated == true && statusFilter === "Consolidated") ||
      (invoice.status === null &&
        statusFilter === "Draft" &&
        invoice.isConsolidated == false);

    const matchesType = typeFilter === "all" || invoice.typeCode === typeFilter;
    const matchesEmailStatus =
      emailStatusFilter == "all" ||
      (emailStatusFilter == "No Status" && invoice.emailStatus == null) ||
      emailStatusFilter == invoice.emailStatus;
    const matchesTag =
      tagFilter === "all" ||
      (tagFilter === "Show" && invoice.isEditedViaSelfService === true) ||
      (tagFilter === "Hide" &&
        (invoice.isEditedViaSelfService === false ||
          invoice.isEditedViaSelfService == null));

    // Issuance Date range
    const matchesDateRange = isWithinDayRange(invoice.invoiceDate, dateRange);

    // Invoice Date range (falls back to issuance date when docDate is null,
    const matchesDocDateRange = isWithinDayRange(
      invoice.docDate ?? invoice.invoiceDate,
      docDateRange,
    );

    return (
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesEmailStatus &&
      matchesTag &&
      matchesDateRange &&
      matchesDocDateRange
    );
  });

  // Calculate statistics
  const stats = {
    // Account Receivable (AR) Amount is all sales related amount (not self billed invoice)
    totalValidARAmount:
      filteredData?.reduce((sum: number, invoice: invoiceList) => {
        if (invoice.status !== "Valid") return sum;

        const amount = Number(invoice.totalPayableAmount);
        switch (invoice.typeCode) {
          case "01":
          case "03":
          case "01A":
            return sum + amount;
          case "02":
          case "04":
            return sum - amount;
          default:
            return sum;
        }
      }, 0) ?? 0,
    // Account Purchasable (AP) Amount is all purchase related amount (self billed invoice)
    totalValidAPAmount:
      filteredData?.reduce((sum: number, invoice: invoiceList) => {
        if (invoice.status !== "Valid") return sum;

        const amount = Number(invoice.totalPayableAmount);
        switch (invoice.typeCode) {
          case "11":
          case "13":
            return sum + amount;
          case "12":
          case "14":
            return sum - amount;
          default:
            return sum;
        }
      }, 0) ?? 0,
    totalInvoices: filteredData?.length ?? 0,
    pendingInvoices:
      filteredData?.filter(
        (invoice: invoiceList) =>
          invoice.status === null && !invoice.isConsolidated,
      ).length ?? 0,
    errorInvoices:
      filteredData?.filter(
        (invoice: invoiceList) =>
          invoice.status === "Invalid" && !invoice.isConsolidated,
      ).length ?? 0,
  };

  // Pagination logic
  const totalPages = Math.ceil((filteredData?.length ?? 0) / itemsPerPage);
  const paginatedData = filteredData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredData) {
      setSelectedInvoices(
        filteredData.map((invoice: invoiceList) => ({
          id: invoice.id,
          status: invoice.status,
        })),
      );
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (
    checked: string | boolean,
    id: string,
    status: string | undefined | null,
  ) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, { id, status }]);
    } else {
      setSelectedInvoices(
        selectedInvoices.filter((selectedId) => selectedId.id !== id),
      );
    }
  };

  const handleBatchDelete = async () => {
    setIsBatchDeleting(true);
    const succeeded: string[] = [];
    const failed: { id: string; message: string }[] = [];

    for (const invoice of selectedInvoices) {
      try {
        await deleteInvoiceAsync(invoice.id);
        succeeded.push(invoice.id);
      } catch (error) {
        let message = "Failed to delete invoice";
        if (error instanceof AxiosError) {
          message = error.response?.data?.message ?? message;
        }
        failed.push({ id: invoice.id, message });
      }
    }

    queryClient.invalidateQueries({ queryKey: ["invoices"] });
    setSelectedInvoices([]);
    setIsBatchDeleting(false);

    if (failed.length === 0) {
      toast({
        title: "Deleted",
        description: `${succeeded.length} invoice(s) have been deleted.`,
        icon: (
          <CheckCircle size={30} className="animate-spin-once text-green-600" />
        ),
        duration: 5000,
      });
      return;
    }

    const detail = failed.map((f) => `• ${f.message}`).join("\n");
    toast({
      title:
        succeeded.length > 0
          ? `${succeeded.length} deleted, ${failed.length} failed`
          : "Delete failed",
      description: detail,
      variant: "destructive",
      icon: <CircleAlert size={30} className="animate-spin-once" />,
      duration: Infinity,
    });
  };

  const handleBatchSendEmail = async () => {
    let success = false;
    let toastTitle = "";
    let toastMessage = "";
    try {
      const response = await api.post("/invoice/send-email", {
        invoiceIds: selectedInvoices.map((invoice) => invoice.id),
      });

      success = true;
      toastTitle = "Success";
      toastMessage =
        response.data.message || "Email sending process started successfully.";
    } catch (error: any) {
      console.error("Error sending email:", error);
      toastTitle = "Error";
      toastMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send email.";
    } finally {
      toast({
        title: toastTitle,
        description: toastMessage,
        duration: 5000,
        variant: success ? "default" : "destructive",
        icon: success ? (
          <CheckCircle size={30} className="animate-spin-once text-green-400" />
        ) : (
          <CircleAlert size={30} className="animate-spin-once" />
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    }
  };

  const handleDelete = async (invoiceId: string) => {
    await deleteInvoiceAsync(invoiceId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        toast({
          title: "Deleted",
          description: "Invoice has been deleted",
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-600"
            />
          ),
          duration: 5000,
        });
      },
      onError: (error) => {
        let errMessage = "Failed to delete invoice";

        if (error instanceof AxiosError) {
          errMessage = error.response?.data.message;
        }
        toast({
          title: "Action failed",
          variant: "destructive",
          description: errMessage,
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: 5000,
        });
      },
    });
  };

  const handleBatchSubmit = async () => {
    try {
      setIsSubmitButtonClicked(true);
      const typeCodeMap: Record<
        DocumentType,
        "01" | "02" | "03" | "04" | "11" | "12" | "13" | "14"
      > = {
        [DocumentType.INVOICE]: "01",
        [DocumentType.CREDIT_NOTE]: "02",
        [DocumentType.DEBIT_NOTE]: "03",
        [DocumentType.REFUND_NOTE]: "04",
        [DocumentType.SELF_BILLED_INVOICE]: "11",
        [DocumentType.SELF_BILLED_CREDIT_NOTE]: "12",
        [DocumentType.SELF_BILLED_DEBIT_NOTE]: "13",
        [DocumentType.SELF_BILLED_REFUND_NOTE]: "14",
        [DocumentType.CONSOLIDATED_INVOICE]: "01", // Convert "01A" to "01"
      };

      // const submitEInvoices = selectedInvoices.map(async (selectedInvoice) => {
      for (const selectedInvoice of selectedInvoices) {
        // Each invoice is isolated
        try {
          //manually call api because cannot use custom hook
          const invoiceDetailsResponse = await api.get<invoiceDetailsApi>(
            `/invoice/${selectedInvoice.id}`,
          );

          if (invoiceDetailsResponse.status != 200) {
            continue;
          }
          const typeCode = typeCodeMap[invoiceDetailsResponse.data.typeCode];
          const invoiceDetails = invoiceDetailsResponse.data;

          const issuanceDateTime = new Date(invoiceDetails.invoiceTime);
          const now = new Date();

          if (issuanceDateTime > now) {
            toast({
              title: "Submission blocked",
              variant: "destructive",
              description: `Invoice ${invoiceDetails.codeNumber}: issuance date is set to a future date. Please update the issuance date before submitting.`,
              duration: 8000,
            });
            continue;
          }

          // Acquire a server-side submission lock
          let lock: { locked: boolean; reason?: string };
          try {
            lock = await acquireSubmissionLock(selectedInvoice.id);
          } catch (lockErr) {
            console.error(
              `Failed to acquire submission lock for ${invoiceDetails.codeNumber}:`,
              lockErr,
            );
            toast({
              title: "Could not verify submission status",
              variant: "destructive",
              description: `${invoiceDetails.codeNumber} was skipped — unable to confirm it is safe to submit. Please try again.`,
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 8000,
            });
            continue;
          }

          if (lock.locked) {
            toast({
              title: "Already submitted",
              description: `${invoiceDetails.codeNumber}: ${lock.reason ?? "this invoice has already been submitted."}`,
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 8000,
            });
            continue;
          }

          const invoiceDetailsToBeSubmit = [
            {
              id: invoiceDetails.codeNumber,
              issueDate: issuanceDateTime.toISOString().split("T")[0],
              issueTime:
                issuanceDateTime.toISOString().split("T")[1].split(".")[0] +
                "Z",
              invoiceTypeCode: typeCode,
              documentCurrencyCode: invoiceDetails.currencyCode,
              taxCurrencyCode: invoiceDetails.currencyCode,

              invoiceStartDate: toLhdnDate(
                invoiceDetails.billingPeriodStartDate,
              ),
              invoiceEndDate: toLhdnDate(invoiceDetails.billingPeriodEndDate),
              invoicePeriodDescription: invoiceDetails.frequencyOfBilling,
              billingReference: invoiceDetails.billReferenceNumber,
              ...(["02", "03", "04", "12", "13", "14"].includes(
                invoiceDetails.typeCode,
              )
                ? {
                    invoiceDocumentReference:
                      invoiceDetails.originalInvoiceReference.map(
                        (originalInvoiceReference) => {
                          return {
                            id: originalInvoiceReference.id,
                            uuid: originalInvoiceReference.uuid,
                          };
                        },
                      ),
                  }
                : {}),

              // additional document reference (import & export)
              additionalDocumentReference: [],

              // supplier
              supplier: {
                additionalAccountID: null, // ATIGA number (import & export)
                classificationCode: invoiceDetails.supplierMsicCode,
                classificationName:
                  invoiceDetails.supplierBusinessActivityDescription,
                identification: [
                  { id: invoiceDetails.supplierTin, schemeId: "TIN" },
                  {
                    id: invoiceDetails.supplierRegistrationNumber,
                    schemeId: invoiceDetails.supplierRegistrationType,
                  },
                  ...(invoiceDetails.supplierSstRegistrationNumber
                    ? [
                        {
                          id: invoiceDetails.supplierSstRegistrationNumber,
                          schemeId: "SST",
                        },
                      ]
                    : []),
                  ...(invoiceDetails.supplierTourismTaxRegistrationNumber
                    ? [
                        {
                          id: invoiceDetails.supplierTourismTaxRegistrationNumber,
                          schemeId: "TTX",
                        },
                      ]
                    : []),
                ],
                postalAddress: {
                  addressLine: [
                    invoiceDetails.supplierAddressLine0,
                    invoiceDetails.supplierAddressLine1,
                    invoiceDetails.supplierAddressLine2,
                  ].filter(Boolean),
                  cityName: invoiceDetails.supplierCity,
                  postalZone: invoiceDetails.supplierPostcode,
                  countrySubentityCode: invoiceDetails.supplierState,
                  country: invoiceDetails.supplierCountry,
                },
                registrationName: invoiceDetails.supplierName,
                telephone: invoiceDetails.supplierContact,
                electronicMail: invoiceDetails.supplierEmail,
              },

              // buyer
              buyer: {
                partyIdentification: [
                  { id: invoiceDetails.buyerTin, schemeId: "TIN" },
                  {
                    id: invoiceDetails.buyerRegistrationNumber,
                    schemeId: invoiceDetails.buyerRegistrationType,
                  },
                  ...(invoiceDetails.buyerSstRegistrationNumber
                    ? [
                        {
                          id: invoiceDetails.buyerSstRegistrationNumber,
                          schemeId: "SST",
                        },
                      ]
                    : []),
                ],
                postalAddress: {
                  addressLine: [
                    invoiceDetails.buyerAddressLine0,
                    invoiceDetails.buyerAddressLine1,
                    invoiceDetails.buyerAddressLine2,
                  ].filter(Boolean),
                  cityName: invoiceDetails.buyerCity,
                  postalZone: invoiceDetails.buyerPostcode,
                  countrySubentityCode: invoiceDetails.buyerState,
                  country: invoiceDetails.buyerCountry,
                },
                registrationName: invoiceDetails.buyerName,
                telephone: invoiceDetails.buyerContact,
                electronicMail: invoiceDetails.buyerEmail,
              },

              // delivery (shipping)
              delivery: null,
              // delivery: {
              //   registrationName: "Recipient's Name",
              //   postalAddress: {
              //     addressLine: ["Jalan 1/91", "Taman Shamelin Perkasa", "Cheras"],
              //     cityName: "Kuala Lumpur",
              //     postalZone: "58000",
              //     countrySubentityCode: "10",
              //     country: "MYS",
              //   },
              //   identification: [
              //     { id: "Recipient's BRN", schemeId: "BRN" },
              //     { id: "C26703685100", schemeId: "TIN" },
              //   ],
              //   shipment: {
              //     id: "SHIP-12345",
              //     freightAllowanceCharge: {
              //       chargeIndicator: true,
              //       amount: 100,
              //       allowanceChargeReason: "Service charge",
              //       multiplierFactorNumeric: null,
              //     },
              //   },
              // },

              // payment means
              ...(invoiceDetails.paymentMode &&
              invoiceDetails.supplierBankAccountNumber
                ? {
                    paymentMeans: {
                      code: invoiceDetails.paymentMode,
                      financialAccount:
                        invoiceDetails.supplierBankAccountNumber,
                    },
                  }
                : { paymentMeans: null }),

              // payment terms
              paymentTerms: invoiceDetails.paymentTerms ?? null,

              // prepaid payment
              ...(invoiceDetails.prepaymentReferenceNumber ||
              invoiceDetails.prepaymentAmount ||
              invoiceDetails.prepaymentDate ||
              invoiceDetails.prepaymentTime
                ? {
                    prepaidPayment: {
                      id: invoiceDetails.prepaymentReferenceNumber ?? null,
                      paidAmount:
                        Number(invoiceDetails.prepaymentAmount) ?? null,
                      paidDate: toLhdnDate(invoiceDetails.prepaymentDate),
                      paidTime: toLhdnTime(invoiceDetails.prepaymentTime),
                    },
                  }
                : {
                    prepaidPayment: null,
                  }),

              // allowanceCharge
              allowanceCharge: [
                ...(invoiceDetails.invoiceAdditionalDiscountAmount
                  ? [
                      {
                        chargeIndicator: false,
                        allowanceChargeReason: "NA", // currently do not have invoice level discount reason
                        amount: Number(
                          invoiceDetails.invoiceAdditionalDiscountAmount,
                        ),
                      },
                    ]
                  : []),
                ...(invoiceDetails.invoiceAdditionalFeeAmount
                  ? [
                      {
                        chargeIndicator: true,
                        allowanceChargeReason: "NA", // currently do not have invoice level fee/charge reason
                        amount: Number(
                          invoiceDetails.invoiceAdditionalFeeAmount,
                        ),
                      },
                    ]
                  : []),
              ],

              // tax total
              taxTotal: {
                taxAmount: Number(invoiceDetails.totalTaxAmount),
                taxSubtotal: [
                  ...invoiceDetails.totalTaxAmountPerTaxType.map(
                    (taxAmountPerTaxType) => {
                      return {
                        taxAmount: taxAmountPerTaxType.taxAmount,
                        taxableAmount: null,
                        taxCategory: {
                          id: taxAmountPerTaxType.taxType,
                          taxExemptionReason: null,
                        },
                      };
                    },
                  ),
                ],
              },

              // legal monetary total
              legalMonetaryTotal: {
                lineExtensionAmount:
                  Number(invoiceDetails.totalNetAmount) ?? null,
                taxExclusiveAmount: Number(invoiceDetails.totalExcludingTax),
                taxInclusiveAmount: Number(invoiceDetails.totalIncludingTax),
                allowanceTotalAmount:
                  Number(invoiceDetails.totalDiscountValue) ?? null,
                chargeTotalAmount:
                  Number(invoiceDetails.totalFeeChargeAmount) ?? null,
                payableRoundingAmount:
                  Number(invoiceDetails.roundingAmount) ?? null,
                payableAmount: Number(invoiceDetails.totalPayableAmount),
              },

              // invoice line item
              invoiceLine: [
                ...invoiceDetails.lineItems.map((lineItem) => ({
                  quantity: Number(lineItem.quantity),

                  unitCode: resolveUomCode(lineItem.measurement),
                  lineExtensionAmount: Number(lineItem.totalExcludingTax),
                  allowanceCharge: [
                    ...(lineItem.feeChargeRate && lineItem.feeChargeRate > 0
                      ? [
                          {
                            chargeIndicator: true,
                            allowanceChargeReason: "Service charge",
                            multiplierFactorNumeric: Number(
                              lineItem.feeChargeRate,
                            ),
                            amount: Number(lineItem.feeChargeAmount),
                          },
                        ]
                      : []),
                    ...(lineItem.discountRate && lineItem.discountRate > 0
                      ? [
                          {
                            chargeIndicator: false,
                            allowanceChargeReason: "Sample Description",
                            multiplierFactorNumeric: Number(
                              lineItem.discountRate,
                            ),
                            amount: Number(lineItem.discountAmount),
                          },
                        ]
                      : []),
                  ],

                  taxTotal: {
                    taxAmount: Number(lineItem.taxAmount),
                    taxSubtotal: {
                      taxAmount: lineItem.taxAmount,
                      taxableAmount:
                        lineItem.taxType == "E"
                          ? lineItem.amountExemptedFromTax
                          : null,
                      baseUnitMeasure: Number(lineItem.quantity),
                      perUnitAmount: Number(lineItem.taxAmount),
                      percent: Number(lineItem.taxRate),
                      taxCategory: {
                        id: lineItem.taxType,
                        taxExemptionReason: lineItem.detailsOfTaxExemption,
                      },
                    },
                  },
                  item: {
                    classificationCode: {
                      class: lineItem.classification,
                      ptc: lineItem.productTariffCode ?? "",
                    },
                    description: lineItem.description,
                    // originCountry: lineItem.countryOfOrigin,
                    originCountry: null,
                  },
                  price: { priceAmount: Number(lineItem.unitPrice) },
                  priceExtension: Number(lineItem.subtotal),
                })),
              ],
            },
          ];

          // The lock is now held
          let lhdnSubmissionResponse: any;
          try {
            lhdnSubmissionResponse = await submitEInvoice({
              invoice: invoiceDetailsToBeSubmit,
              user,
            });
          } catch (submitErr: any) {
            console.error(
              `Submit request failed for ${invoiceDetails.codeNumber}:`,
              submitErr,
            );

            // Only an UNKNOWN outcome justifies PendingCheck.
            const httpStatus: number | undefined = submitErr?.__httpStatus;
            const provenNotSent =
              typeof httpStatus === "number" &&
              httpStatus >= 400 &&
              httpStatus < 500;

            if (provenNotSent) {
              await releaseSubmissionLock(selectedInvoice.id);
              toast({
                title:
                  httpStatus === 429
                    ? "Rate limit reached"
                    : "Submission failed",
                variant: "destructive",
                description:
                  httpStatus === 429
                    ? `${invoiceDetails.codeNumber} was not sent (too many requests). Please try again in a moment.`
                    : `${invoiceDetails.codeNumber} was not sent to LHDN (${httpStatus}). Please try again.`,
                icon: <CircleAlert size={30} className="animate-spin-once" />,
                duration: 8000,
              });
              queryClient.invalidateQueries({ queryKey: ["invoices"] });
              continue;
            }

            // Network failure / timeout / 5xx
            await markPendingCheck(selectedInvoice.id, user.tin);
            toast({
              title: "Submission status unknown",
              variant: "destructive",
              description: `${invoiceDetails.codeNumber}: no response from LHDN. We'll verify the real status — do NOT resubmit yet.`,
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 10000,
            });
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            continue;
          }

          if (lhdnSubmissionResponse?.statusCode == "429") {
            // Never reached LHDN
            await releaseSubmissionLock(selectedInvoice.id);
            toast({
              title: "Rate Limit",
              description: `${lhdnSubmissionResponse.message ?? "Too many requests. Please try again shortly."}`,
              variant: "destructive",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 5000,
            });
            continue;
          }

          const acceptedDoc = lhdnSubmissionResponse?.acceptedDocuments?.[0];
          const submissionUid = lhdnSubmissionResponse?.submissionUid ?? null;
          const uuid = acceptedDoc?.uuid ?? null;
          const hasRejectedDocs =
            (lhdnSubmissionResponse?.rejectedDocuments?.length ?? 0) > 0;

          // LHDN returned a pure error (no submissionUid, no accepted, no
          // rejected)
          if (!submissionUid && !uuid && !hasRejectedDocs) {
            try {
              await processEInvoice({
                invoiceId: selectedInvoice.id,
                onBehalfOf: user.tin,
                lhdnSubmissionResponse,
              });
            } catch (processErr) {
              console.error(
                `Background processing failed for ${invoiceDetails.codeNumber}:`,
                processErr,
              );
            }
            toast({
              title: "Submission needs review",
              variant: "destructive",
              description: `${invoiceDetails.codeNumber} was not accepted by LHDN. We're reconciling its status — check again shortly before resubmitting.`,
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 10000,
            });
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            continue;
          }

          toast({
            title: "Submit invoice",
            description: `${invoiceDetails.codeNumber} is submitted`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });

          const body = {
            internalId: invoiceDetails.codeNumber,
            invoiceId: selectedInvoice.id,
            onBehalfOf: user.tin,
            uuid,
            submissionUid,
            lhdnSubmissionResponse,
          };

          await insertTemporarySubmissionStatus(body);
          queryClient.invalidateQueries({ queryKey: ["invoices"] });

          // Settle the status after a short delay.
          if (uuid) {
            (async () => {
              await new Promise((resolve) => setTimeout(resolve, 10000));
              try {
                await syncInvoiceStatus(selectedInvoice.id);
              } catch (err) {
                console.error(
                  `Post-submit status sync failed for ${selectedInvoice.id}:`,
                  err,
                );
              }
              queryClient.invalidateQueries({ queryKey: ["invoices"] });
            })();
          }
        } catch (invoiceErr) {
          // Safety net
          console.error(
            `Batch submit failed for invoice ${selectedInvoice.id}:`,
            invoiceErr,
          );
          toast({
            title: "Failed",
            variant: "destructive",
            description: `Could not submit one of the selected invoices. It was skipped; please retry it individually.`,
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 6000,
          });
          continue;
        }
      }
    } finally {
      setSelectedInvoices([]);
      setIsSubmitButtonClicked(false);
    }
  };

  const getStatusColor = (status: string) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Draft":
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case "Submitted":
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case "Valid":
        return `${baseStyle} bg-green-100 text-green-800`;
      case "Invalid":
        return `${baseStyle} bg-red-100 text-red-800`;
      case "Consolidated":
        return `${baseStyle} bg-stone-200 text-stone-800`;
      // In-flight / awaiting reconciliation
      case "Submitting":
        return `${baseStyle} bg-indigo-100 text-indigo-800`;
      case "PendingCheck":
        return `${baseStyle} bg-amber-100 text-amber-900`;
      case "DuplicateConflict":
        return `${baseStyle} bg-orange-100 text-orange-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  // Human-friendly label
  const getStatusLabel = getInvoiceStatusLabel;

  const getEmailStatusColor = (status: string) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Sending":
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case "Sent":
        return `${baseStyle} bg-green-100 text-green-800`;
      case "Error":
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return ``;
    }
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5; // Adjust this as needed

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisibleButtons / 2),
    );
    const endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <Button
          key="1"
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>,
      );

      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end">...</span>);
      }

      buttons.push(
        <Button
          key={totalPages}
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>,
      );
    }

    return buttons;
  };

  const getExpiredReminder = () => {
    const expiryDate = new Date(expiredDate?.data.expiredAt);
    if (!expiredDate) {
      return; // staging wont have record for license
    }
    const formatted = format(expiryDate, "dd/MM/yyyy");

    const daysLeft = differenceInDays(expiryDate, new Date());

    if (daysLeft <= 14) {
      return (
        <Callout.Root size="1" className="-my-4 !bg-yellow-100">
          <Callout.Icon>
            <TriangleAlert className="text-amber-700" />
          </Callout.Icon>
          <Callout.Text className="text-amber-700">
            {daysLeft > 0 ? (
              <>
                Your Quickbill subscription ends on <strong>{formatted}</strong>{" "}
                ({daysLeft} day{daysLeft !== 1 ? "s" : ""} left), please renew
                your subscription to avoid interruption.
              </>
            ) : daysLeft === 0 ? (
              <>
                Your Quickbill subscription <strong>expires today</strong> (
                <strong>{formatted}</strong>), please renew your subscription to
                avoid interruption.
              </>
            ) : (
              <>
                Your Quickbill subscription ended on{" "}
                <strong>{formatted}</strong> ({Math.abs(daysLeft)} day
                {Math.abs(daysLeft) !== 1 ? "s" : ""} overdue), please renew
                your subscription to avoid interruption.
              </>
            )}
          </Callout.Text>
        </Callout.Root>
      );
    }
  };

  const handleCloneInvoice = () => {
    setIsCloneInvoiceLoading(true);
    const selectedInvoiceId = selectedInvoices.map((invoice) => invoice.id);
    cloneInvoice(selectedInvoiceId, {
      onSuccess: (response) => {
        const cloneResult = response.data;
        queryClient.invalidateQueries({ queryKey: ["invoices"] });

        if (cloneResult.createdNewInvoices.length > 0) {
          toast({
            title: "Successfully Cloned Invoice",
            description: `${response.data.createdNewInvoices.map((inv: { id: string; codeNumber: string }) => inv.codeNumber).join(", ")} have been created`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-600"
              />
            ),
            duration: 5000,
          });
        }

        if (cloneResult.skippedInvoices.length > 0) {
          toast({
            title: "Unable to clone following invoice",
            variant: "destructive",
            description: `${cloneResult.skippedInvoices.map((inv: { id: string; codeNumber: string }) => inv.codeNumber).join(", ")} have been skipped`,
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 5000,
          });
        }
        setSelectedInvoices([]);
        setIsCloneInvoiceLoading(false);
      },
      onError: (error) => {
        let errMessage = "Failed to clone invoice";

        if (error instanceof AxiosError) {
          errMessage = error.response?.data.message;
        }
        toast({
          title: "Action failed",
          variant: "destructive",
          description: errMessage,
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: 5000,
        });

        setSelectedInvoices([]);
        setIsCloneInvoiceLoading(false);
      },
    });
  };

  const activeFilters = [
    statusFilter !== "all" && { type: "Status", value: statusFilter },
    typeFilter !== "all" && {
      type: "Type",
      value: invoiceTypes.find((t) => t.Code === typeFilter)?.Description,
    },
    tagFilter !== "all" && { type: "Self-service Tag", value: tagFilter },
    emailStatusFilter !== "all" && {
      type: "Email Status",
      value: emailStatusFilter,
    },
    dateRange.from && {
      type: "Issuance From",
      value: format(dateRange.from, "PP"),
    },
    dateRange.to && { type: "Issuance To", value: format(dateRange.to, "PP") },
    docDateRange.from && {
      type: "Invoice From",
      value: format(docDateRange.from, "PP"),
    },
    docDateRange.to && {
      type: "Invoice To",
      value: format(docDateRange.to, "PP"),
    },
  ].filter(Boolean);

  return (
    <div className="w-full space-y-6">
      {getExpiredReminder()}
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex flex-row gap-2">
              <CardTitle className="text-sm font-medium">
                Total Valid AR Amount
              </CardTitle>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={16} className="text-slate-700" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72 border-slate-300">
                    <p className="mb-2">
                      Total = (Invoices + Debit Notes) - (Credit Notes + Refund
                      Notes)
                    </p>
                    <p>
                      Only{" "}
                      <span className="font-bold text-green-600">
                        validated
                      </span>{" "}
                      documents are included.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {/* regex to add comma for every thousand */}
              RM {formatTotalAmount(stats.totalValidARAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total valid AR invoice value
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex flex-row gap-2">
              <CardTitle className="text-sm font-medium">
                Total Valid AP Amount
              </CardTitle>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={16} className="text-slate-700" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72 border-slate-300">
                    <p className="mb-2">
                      Total = (Self Billed Invoices + Self Billed Debit Notes) -
                      (Self Billed Credit Notes + Self Billed Refund Notes)
                    </p>
                    <p>
                      Only{" "}
                      <span className="font-bold text-green-600">
                        validated
                      </span>{" "}
                      documents are included.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              RM {formatTotalAmount(stats.totalValidAPAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total valid AP invoice value
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Invoices
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalInvoices}</div>
            <p className="text-xs text-muted-foreground">Across all statuses</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Drafts
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingInvoices}</div>
            <p className="text-xs text-muted-foreground">Awaiting submission</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error States</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.errorInvoices}</div>
            <p className="text-xs text-muted-foreground">Require attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col gap-4">
        {/* Top row with filters and actions */}
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-4">
            <Input
              placeholder="Search invoice, bill ref, buyer, supplier"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-sm"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses
                  .filter((status) => status !== "all")
                  .map((status) => (
                    <SelectItem key={status} value={status}>
                      <div className="flex items-center gap-2">
                        {/* value stays the raw status  */}
                        <span className={getStatusColor(status)}>
                          {getStatusLabel(status)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Invoice type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {invoiceTypes.map((type) => (
                  <SelectItem key={type.Code} value={type.Code}>
                    {type.Description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={emailStatusFilter}
              onValueChange={setEmailStatusFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Invoice type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Email Status</SelectItem>
                <SelectItem value="No Status">No Status</SelectItem>
                <SelectItem value="Sent">Sent</SelectItem>
                <SelectItem value="Error">Error</SelectItem>
                <SelectItem value="Sending">Sending</SelectItem>
              </SelectContent>
            </Select>

            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Invoice type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                <SelectItem value="Show">Self-Service Tag</SelectItem>
                <SelectItem value="Hide">Non Self-Service Tag</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !dateRange.from && !dateRange.to && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "PP")} -{" "}
                        {format(dateRange.to, "PP")}
                      </>
                    ) : (
                      format(dateRange.from, "PP")
                    )
                  ) : (
                    "Issuance date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) =>
                    setDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !docDateRange.from &&
                      !docDateRange.to &&
                      "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {docDateRange.from ? (
                    docDateRange.to ? (
                      <>
                        {format(docDateRange.from, "PP")} -{" "}
                        {format(docDateRange.to, "PP")}
                      </>
                    ) : (
                      format(docDateRange.from, "PP")
                    )
                  ) : (
                    "Invoice date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={docDateRange}
                  onSelect={(range) =>
                    setDocDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Column visibility control */}
          <DropdownMenu
            open={dropdownOpenVisibleColumn}
            onOpenChange={(val) => setDropdownOpenVisibleColumn(val)}
          >
            <DropdownMenuTrigger
              onClick={() => {
                setDropdownOpenVisibleColumn((val) => !val);
              }}
              asChild
            >
              <Button variant="outline" className="ml-4">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibleColumns).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleColumns((prev) => ({ ...prev, [key]: checked }))
                  }
                >
                  {columnLabels[key] ?? key}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Batch actions in fixed height container */}
        <div className="h-9">
          {" "}
          {/* Fixed height container */}
          {selectedInvoices.length > 0 && (
            <div className="flex items-center gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isBatchDeleting}
                  >
                    {isBatchDeleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting
                      </>
                    ) : (
                      <>
                        <Trash className="mr-2 h-4 max-w-4" />
                        Delete Selected ({selectedInvoices.length})
                      </>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete Selected Invoices
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      You are about to permanently delete{" "}
                      {selectedInvoices.length} selected invoice(s). This cannot
                      be recovered once deleted. Invoices that are already
                      Submitted, Valid, or consolidated into another invoice
                      will be skipped automatically.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBatchDelete}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {!selectedInvoices.some((inv) =>
                [
                  "Valid",
                  "Submitted",
                  "Cancelled",
                  "Rejected",
                  "Consolidated",
                  // In-flight / awaiting reconciliation with LHDN — must not be
                  // resubmitted from the UI
                  "Submitting",
                  "PendingCheck",
                ].includes(inv.status ?? ""),
              ) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBatchSubmit}
                  disabled={isSubmitting || isSubmitButtonClicked}
                >
                  {isSubmitting || isSubmitButtonClicked ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Submitting
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Selected ({selectedInvoices.length})
                    </>
                  )}
                </Button>
              )}

              <Button
                variant="secondary"
                size="sm"
                onClick={handleBatchSendEmail}
              >
                <Mails className="mr-2 h-4 w-4" />
                Send Invoice({selectedInvoices.length})
              </Button>

              <Button
                variant="secondary"
                size="sm"
                disabled={isCloneInvoiceLoading}
                onClick={handleCloneInvoice}
              >
                {isCloneInvoiceLoading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Cloning Document
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Clone Invoice ({selectedInvoices.length})
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <Badge
                key={filter ? `${filter.type}-${filter.value}` : undefined}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {filter && `${filter.type}: ${filter.value}`}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => {
                    if (filter) {
                      switch (filter.type) {
                        case "Status":
                          setStatusFilter("all");
                          break;
                        case "Type":
                          setTypeFilter("all");
                          break;
                        case "Email Status":
                          setEmailStatusFilter("all");
                          break;
                        case "Self-service Tag":
                          setTagFilter("all");
                          break;
                        case "Issuance From":
                        case "Issuance To":
                          setDateRange({ from: undefined, to: undefined });
                          break;
                        case "Invoice From":
                        case "Invoice To":
                          setDocDateRange({ from: undefined, to: undefined });
                          break;
                      }
                    }
                  }}
                />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
                setTypeFilter("all");
                setEmailStatusFilter("all");
                setTagFilter("all");
                setDateRange({ from: undefined, to: undefined });
                setDocDateRange({ from: undefined, to: undefined });
                setFilter("");
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {!filteredData && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100">
            <CircleX className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No invoice</h3>
          <p className="mt-2 text-sm text-gray-500">
            You do not have any invoice currently. Start by creating your first
            invoice
          </p>
        </div>
      )}

      {/* Empty State */}
      {filteredData && filteredData.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
            <Filter className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No results found</h3>
          <p className="mt-2 text-sm text-gray-500">
            Try adjusting your search or filter to find what you're looking for.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setFilter("");
              setStatusFilter("all");
              setTypeFilter("all");
              setDateRange({ from: undefined, to: undefined });
              setDocDateRange({ from: undefined, to: undefined });
            }}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Table */}
      {filteredData && filteredData.length > 0 && (
        <>
          <div className="rounded-md border shadow-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedInvoices.length === filteredData.length &&
                        selectedInvoices.length !== 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.supplier && <TableHead>Supplier</TableHead>}
                  {visibleColumns.buyer && <TableHead>Buyer</TableHead>}
                  {visibleColumns.codeNumber && (
                    <TableHead>Invoice Number</TableHead>
                  )}
                  {visibleColumns.postReferenceNumber && (
                    <TableHead>POS Reference Number</TableHead>
                  )}
                  {visibleColumns.docDate && (
                    <TableHead>Invoice Date</TableHead>
                  )}
                  {visibleColumns.invoiceDate && (
                    <TableHead>Issuance Date</TableHead>
                  )}
                  {visibleColumns.invoiceTime && (
                    <TableHead>Invoice Time</TableHead>
                  )}
                  {visibleColumns.amount && (
                    <TableHead className="text-right">Amount</TableHead>
                  )}
                  {visibleColumns.status && <TableHead>Status</TableHead>}
                  {visibleColumns.emailStatus && (
                    <TableHead>Email Status</TableHead>
                  )}
                  {visibleColumns.selfServiceTag && (
                    <TableHead className="min-w-32">Self-Service Tag</TableHead>
                  )}
                  {visibleColumns.uuid && <TableHead>UUID</TableHead>}
                  {visibleColumns.actions && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData &&
                  paginatedData.map((invoice: invoiceList) => (
                    <TableRow
                      key={invoice.id}
                      onDoubleClick={() => {
                        navigate(`/invoice-details/${invoice.typeCode}`, {
                          state: { id: invoice.id },
                        });
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedInvoices.some(
                            (inv) => inv.id === invoice.id,
                          )}
                          onCheckedChange={(checked) =>
                            handleSelectInvoice(
                              checked,
                              invoice.id,
                              invoice.isConsolidated
                                ? "Consolidated"
                                : invoice?.status,
                            )
                          }
                        />
                      </TableCell>
                      {visibleColumns.supplier && (
                        <TableCell className="font-medium">
                          {invoice.supplierName ?? "Not Found"}
                          <div className="text-xs text-gray-500">
                            {invoice.supplierCode}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.buyer && (
                        <TableCell>
                          {invoice.buyerName ?? "Not Found"}
                          <div className="text-xs text-gray-500">
                            {invoice.buyerCode}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.codeNumber && (
                        <TableCell>{invoice.codeNumber}</TableCell>
                      )}
                      {visibleColumns.postReferenceNumber && (
                        <TableCell>{invoice.postReferenceNumber}</TableCell>
                      )}
                      {visibleColumns.docDate && (
                        <TableCell>
                          {format(
                            new Date(invoice.docDate ?? invoice.invoiceDate),
                            "dd/MM/yyyy",
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.invoiceDate && (
                        <TableCell>
                          {format(new Date(invoice.invoiceDate), "dd/MM/yyyy")}
                        </TableCell>
                      )}
                      {visibleColumns.invoiceTime && (
                        <TableCell>
                          {new Date(invoice.invoiceTime).toLocaleTimeString()}
                        </TableCell>
                      )}
                      {visibleColumns.amount && (
                        <TableCell className="text-right">
                          {formatTotalAmount(
                            Number(invoice.totalPayableAmount),
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <span
                            className={getStatusColor(
                              invoice.isConsolidated
                                ? "Consolidated"
                                : (invoice?.status ?? "Draft"),
                            )}
                          >
                            {/* <span className={getStatusColor(invoice?.status ?? (
                            invoice.isConsolidated ? "Consolidated" : "Draft"
                          ))}> */}
                            {/* {invoice?.status ?? (
                            invoice.isConsolidated? "Consolidated" : "Draft"
                          )} */}
                            {invoice.isConsolidated
                              ? "Consolidated"
                              : getStatusLabel(invoice?.status ?? "Draft")}
                          </span>
                        </TableCell>
                      )}
                      {visibleColumns.emailStatus && (
                        <TableCell>
                          <span
                            className={getEmailStatusColor(
                              invoice.emailStatus ?? "",
                            )}
                          >
                            {invoice.emailStatus ?? null}
                          </span>
                        </TableCell>
                      )}
                      {visibleColumns.selfServiceTag && (
                        <TableCell>
                          {invoice.isEditedViaSelfService && (
                            <span className="rounded-full px-3 py-1 text-xs font-medium text-cetner bg-green-100 text-green-800">
                              {invoice.isEditedViaSelfService == true &&
                                "Self-Service"}
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.uuid && (
                        <TableCell>{invoice.uuid}</TableCell>
                      )}
                      {visibleColumns.actions && (
                        <TableCell>
                          <DropdownMenu
                            open={openDropdownRowId === invoice.id}
                            onOpenChange={(isOpen) => {
                              setOpenDropdownRowId(isOpen ? invoice.id : null);
                            }}
                          >
                            <DropdownMenuTrigger
                              asChild
                              onClick={() => {
                                setOpenDropdownRowId((prev) =>
                                  prev == invoice.id ? null : invoice.id,
                                );
                              }}
                            >
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant={"ghost"}
                                    onClick={() =>
                                      navigate(
                                        `/invoice-details/${invoice.typeCode}`,
                                        { state: { id: invoice.id } },
                                      )
                                    }
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Details
                                  </Button>
                                </DialogTrigger>
                              </Dialog>
                              {!(
                                invoice.status == "Valid" ||
                                invoice.status == "Submitted" ||
                                invoice.status == "Pending Validation" ||
                                invoice.status == "Rejected"
                              ) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        className="w-full justify-start text-red-600"
                                        variant={"ghost"}
                                      >
                                        <Trash className="mr-2 h-4 w-4" />
                                        Delete
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          Delete Invoice
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete
                                          invoice {invoice.codeNumber}? This
                                          action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>
                                          Cancel
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={async () => {
                                            await handleDelete(invoice.id);
                                          }}
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-2">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {getPaginationButtons()}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceTable;
